import {FC, useEffect, useState} from 'react'
import {Button} from 'react-bootstrap'
import {ArrowLeftShort, List, X} from 'react-bootstrap-icons'
import {Link, useLocation} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import MobileNavOffcanvas from './MobileNavOffcanvas'
import {triggerHapticFeedback} from './utils/touchOptimization'
import {announceToScreenReader} from './utils/navigationHelpers'
import {useShouldAnimate} from './hooks/useReducedMotion'
import WeatherWarningButton from '../weather/WeatherWarningButton'
import {canManageWeather} from '../weather/weatherAccess'
import styles from './navigation.module.css'
import type {User} from '../../types/auth'
import {useNavBack} from '../../contexts/NavBackContext'

const getParentPath = (pathname: string): string | null => {
  const subPageMap: Record<string, string> = {
    '/profile/payments': '/profile',
    '/scorecard': '/dashboard',
    '/scorecard/multiplayer': '/scorecard'
  }
  if (subPageMap[pathname]) return subPageMap[pathname]
  if (pathname.startsWith('/scorecard-view/')) return '/dashboard'
  if (pathname.startsWith('/friends/scorecard/')) return '/dashboard'
  if (pathname.startsWith('/friends/profile/')) return '/friends'
  if (pathname.startsWith('/player/rankings/')) return '/player/rankings'
  return null
}

const getPageTitle = (pathname: string): string => {
  const pathToKey: Record<string, string> = {
    '/dashboard': 'navigation.roles.PLAYER.items.dashboard',
    '/profile': 'breadcrumbs.profile',
    '/friends': 'navigation.roles.PLAYER.items.friends',
    '/competitions': 'navigation.roles.PLAYER.items.competitions',
    '/player/rankings': 'navigation.roles.PLAYER.items.rankings',
    '/rankings': 'navigation.roles.MANAGER.items.rankings',
    '/stats': 'navigation.roles.PLAYER.items.stats',
    '/administration': 'breadcrumbs.administration',
    '/settings': 'breadcrumbs.settings',
    '/users': 'breadcrumbs.user_management',
    '/tenants': 'navigation.roles.SUPERUSER.items.tenants'
  }

  // Check for dynamic paths
  if (pathname.startsWith('/player/rankings/')) {
    return 'navigation.roles.PLAYER.items.rankings'
  }
  if (pathname.startsWith('/rankings/')) {
    return 'navigation.roles.MANAGER.items.rankings'
  }

  return pathToKey[pathname] || ''
}

interface MobileNavigationProps {
  user: User;
}

const MobileNavigation: FC<MobileNavigationProps> = ({user}) => {
  const [showMenu, setShowMenu] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const shouldAnimate = useShouldAnimate()
  const location = useLocation()
  const {t} = useTranslation()

  const showWeatherButton = canManageWeather(user)

  const pageTitle = t(getPageTitle(location.pathname))
  const stateBackTo = (location.state as {backTo?: string} | null)?.backTo
  const parentPath = stateBackTo ?? getParentPath(location.pathname)
  const {backHandler} = useNavBack()

  const handleMenuToggle = (): void => {
    if (shouldAnimate && isAnimating) return

    triggerHapticFeedback('light')

    if (shouldAnimate) {
      setIsAnimating(true)
      setTimeout(() => setIsAnimating(false), 300)
    }

    setShowMenu(true)
  }

  const handleMenuClose = (): void => {
    if (shouldAnimate && isAnimating) return

    if (shouldAnimate) {
      setIsAnimating(true)
      setTimeout(() => setIsAnimating(false), 300)
    }

    setShowMenu(false)
  }

  useEffect(() => {
    if (showMenu) {
      announceToScreenReader('Navigation menu opened')
    }
  }, [showMenu])

  return (
    <>
      <div className="d-flex align-items-center py-2 px-3 w-100" style={{gap: '0.5rem'}}>
        {/* Back arrow or page title on left */}
        <div style={{flex: '1 1 0', minWidth: 0}}>
          {backHandler ? (
            <button
              type="button"
              onClick={backHandler}
              style={{color: '#FAFAFA', display: 'flex', alignItems: 'center', background: 'none', border: 'none', padding: 0, cursor: 'pointer'}}
            >
              <ArrowLeftShort size={28}/>
            </button>
          ) : parentPath ? (
            <Link to={parentPath} style={{color: '#FAFAFA', display: 'flex', alignItems: 'center'}}>
              <ArrowLeftShort size={28}/>
            </Link>
          ) : (
            <span style={{
              color: '#FAFAFA',
              fontSize: '1rem',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block'
            }}>
              {pageTitle}
            </span>
          )}
        </div>

        {/* Impact logo centered - navigates to dashboard */}
        <Link to="/dashboard" style={{flex: '0 0 auto'}}>
          <img
            src="/impact_logo-1x.png"
            srcSet="/impact_logo-1x.png 1x, /impact_logo-2x.png 2x"
            alt="Impact"
            style={{height: '24px', width: 'auto'}}
          />
        </Link>

        {/* Weather button + Hamburger menu on right */}
        <div style={{flex: '1 1 0', display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
          {showWeatherButton && (
            <WeatherWarningButton tenantId={user.tenantId as string} />
          )}
          <Button
            variant="link"
            className={`p-2 ${styles.hamburgerBtn} ${isAnimating ? styles.animating : ''}`}
            onClick={handleMenuToggle}
            aria-label={showMenu ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={showMenu}
            style={{
              minHeight: '44px',
              minWidth: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              color: '#FAFAFA'
            }}
          >
            <div className={styles.hamburgerIconWrapper}>
              <List
                size={28}
                color="#FAFAFA"
                className={`${styles.hamburgerIcon} ${showMenu ? styles.fadeOut : styles.fadeIn}`}
              />
              <X
                size={28}
                color="#FAFAFA"
                className={`${styles.closeIcon} ${showMenu ? styles.fadeIn : styles.fadeOut}`}
              />
            </div>
          </Button>
        </div>
      </div>

      <MobileNavOffcanvas
        show={showMenu}
        onHide={handleMenuClose}
        user={user}
      />
    </>
  )
}

export default MobileNavigation
